// FILE: src/hooks/useFreeLimits.js
import { useEffect, useState, useCallback } from "react";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";

const FREE_LIMIT = 1;

export function isFreePlan(user) {
  return (
    user?.subscriptionStatus === "free" ||
    user?.planKey === "free"
  );
}

/**
 * Returns limit status for each free-tier section.
 * Only runs checks if user is on free plan.
 */
export function useFreeLimits(user) {
  const free = isFreePlan(user);
  const uid = user?.uid;

  const [limits, setLimits] = useState({
    documents: false,
    messages: false,
    funeralPlanning: false,
    passwordVault: false,
    accountManagers: false,
  });
  const [loading, setLoading] = useState(free);

  const refresh = useCallback(async () => {
    if (!free || !uid) { setLoading(false); return; }
    try {
      const [docsSnap, msgsSnap, funeralSnap, vaultSnap, grantsSnap] = await Promise.all([
        getDocs(query(collection(db, "documents"), where("uid", "==", uid))),
        getDocs(query(collection(db, "personalMessages"), where("uid", "==", uid))),
        getDoc(doc(db, "funeralPlans", uid)),
        getDoc(doc(db, "passwordVault", uid)),
        getDocs(query(collection(db, "grants"), where("ownerUid", "==", uid))),
      ]);

      const vaultEntries = vaultSnap.exists() ? (vaultSnap.data().entries || []).length : 0;
      const funeralExists = funeralSnap.exists() && Object.keys(funeralSnap.data()).length > 2;

      setLimits({
        documents: docsSnap.size >= FREE_LIMIT,
        messages: msgsSnap.size >= FREE_LIMIT,
        funeralPlanning: funeralExists,
        passwordVault: vaultEntries >= FREE_LIMIT,
        accountManagers: grantsSnap.size >= FREE_LIMIT,
      });
    } catch (e) {
      console.error("[useFreeLimits]", e);
    }
    setLoading(false);
  }, [free, uid]);

  useEffect(() => { refresh(); }, [refresh]);

  return { free, limits, loading, refresh };
}
